import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  Slider,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Draggable from "react-draggable";
import asset from "json/assets";
import events from "json/events";
import { useEventEmitter } from "hooks/useEventEmitter";
import popOverStyles from "styles/component/CustomCoursePopOver.module.scss";
import styles from "styles/component/ImageOverlayTool.module.scss";
import { MAX_OVERLAY_IMAGES } from "./useImageOverlays";

const ImageOverlayPanel = ({
  overlays,
  editingId,
  addImages,
  removeOverlay,
  toggleVisible,
  setOverlayOpacity,
  startEdit,
  stopEdit,
}) => {
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEventEmitter(events.openImageOverlayPanel, (e) => {
    setOpen(e?.visible ?? true);
  });

  const handleClose = () => {
    stopEdit();
    setOpen(false);
  };

  const handleFilesSelected = (e) => {
    addImages(e.target.files);
    e.target.value = "";
  };

  if (!open) return null;

  const atCapacity = overlays.length >= MAX_OVERLAY_IMAGES;

  const overlayRows = overlays.map((overlay) => {
    const isEditing = editingId === overlay.id;
    return (
      <li
        key={overlay.id}
        className={isEditing ? styles.editing_row : undefined}
      >
        <Box className={styles.row_main}>
          <span className={styles.overlay_name} title={overlay.name}>
            {overlay.name}
          </span>
          <span className={styles.row_actions}>
            <Tooltip title={overlay.visible ? "Hide" : "Show"} arrow placement="top">
              <Button onClick={() => toggleVisible(overlay.id)}>
                {overlay.visible ? (
                  <VisibilityIcon className={styles.eye_icon} />
                ) : (
                  <VisibilityOffIcon className={styles.eye_icon} />
                )}
              </Button>
            </Tooltip>
            {isEditing ? (
              <Tooltip title="Done" arrow placement="top">
                <Button onClick={stopEdit}>
                  <CheckCircleIcon className={styles.done_icon} />
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Edit" arrow placement="top">
                <Button onClick={() => startEdit(overlay.id)}>
                  <img src={asset.pencilicon} alt="" />
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Remove" arrow placement="top">
              <Button onClick={() => removeOverlay(overlay.id)}>
                <img src={asset.close} alt="" />
              </Button>
            </Tooltip>
          </span>
        </Box>
        <Box className={styles.opacity_row}>
          <Typography variant="caption" className={styles.opacity_label}>
            Opacity
          </Typography>
          <Slider
            size="small"
            min={0}
            max={100}
            value={Math.round(overlay.opacity * 100)}
            onChange={(e, value) => setOverlayOpacity(overlay.id, value / 100)}
            valueLabelDisplay="auto"
          />
        </Box>
      </li>
    );
  });

  return (
    <Draggable
      bounds={"#DraggableContainer"}
      defaultPosition={{ x: 0, y: 0 }}
      cancel=".MuiSlider-root, button, input"
    >
      <Box
        className={`${popOverStyles.poover_content} ${popOverStyles.default_poover_content} ${styles.overlay_panel} custom_popover_content`}
      >
        <Box className={popOverStyles.poover_head}>
          <Typography variant="h5">Image Overlays</Typography>
          <List>
            <ListItem>
              <Tooltip
                title={atCapacity ? `Maximum ${MAX_OVERLAY_IMAGES} images` : "Add Image"}
                arrow
                placement="top"
              >
                <span>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={atCapacity}
                  >
                    <img src={asset.plus} alt="" />
                  </Button>
                </span>
              </Tooltip>
            </ListItem>
            <ListItem>
              <Tooltip title="Close" arrow placement="top">
                <Button onClick={handleClose}>
                  <img src={asset.close} alt="" />
                </Button>
              </Tooltip>
            </ListItem>
          </List>
        </Box>
        <Box className={popOverStyles.popover_body}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            hidden
            onChange={handleFilesSelected}
          />
          {overlays.length === 0 ? (
            <Typography className={styles.empty_text} variant="body2">
              Click + to add an image on the map.
            </Typography>
          ) : (
            <ul className={styles.overlay_list}>{overlayRows}</ul>
          )}
        </Box>
      </Box>
    </Draggable>
  );
};

export default ImageOverlayPanel;
